import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChangedArea,
  FieldKeys,
  useGetProductGroupsForSourcingEventQuery,
  useListTextDocumentsForSourcingEventV2Query,
  usePublishPrivateSourcingEventMutation,
  useCancelEventChangeMutation,
  useSubmitEventChangeMutation,
  type FieldStatusDto,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../../autogen/bff-api";
import { useEditor } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../../../../common/formatting/displayDateWithTime";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { useSub } from "../../../../../../common/subscription/useSub";
import { useConfirmationModal } from "../../../../../../common/useConfirmationModal";
import { urls } from "../../../../../../urls";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import { EmptyValue, SummaryField } from "../../components/summary/SummaryField";
import { useReadinessByField } from "../../hooks/useReadinessByField";
import { openChangeDraft } from "../../../../../v2/sourcing-event/changes/changeScope";
import { includesAwardCriteria, includesMinimumRequirements, resolveInclusions } from "../../wizardSteps";

interface Props {
  eventId: string;
  previousStep?: () => void;
}

const orderedByUnmet = (statuses: ReadonlyArray<FieldStatusDto | undefined>) => [
  ...statuses.filter((status) => status && !status.satisfied),
  ...statuses.filter((status) => status?.satisfied),
];

export const ReviewAndPublishStep = ({ eventId, previousStep }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const { state } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { readinessFor, isPublishReady, isLoading: readinessLoading, isError: readinessError } = useReadinessByField(eventId);
  const [publishPrivate, { isLoading: isPublishing }] = usePublishPrivateSourcingEventMutation();
  const [submitChange, { isLoading: isSendingChange }] = useSubmitEventChangeMutation();
  const [cancelChange, { isLoading: isDiscarding }] = useCancelEventChangeMutation();
  const changeDraft = openChangeDraft(state);
  const isChangeMode = changeDraft !== undefined;

  const privateContext = state.eventContext.type === "Private" ? state.eventContext : null;
  const organizationInviteCount = privateContext?.organizationInvites.length ?? 0;
  const emailInviteCount = privateContext?.emailInvites?.length ?? 0;
  const invitedCount = organizationInviteCount + emailInviteCount;
  const { data: textDocuments } = useListTextDocumentsForSourcingEventV2Query({ eventId });
  const documentCount = (state.eventData.documents?.length ?? 0) + (textDocuments?.documents.length ?? 0);
  const { hasProductAccess } = useSub();
  const productsIncluded = hasProductAccess && resolveInclusions(state.eventData).products;
  const { data: productData } = useGetProductGroupsForSourcingEventQuery(
    { sourcingEventId: eventId },
    { skip: !productsIncluded },
  );
  const productGroups = productData?.productGroups ?? [];
  const productCount = productGroups.reduce((sum, group) => sum + group.products.length, 0);
  const awardCriteria = state.eventData.awardCriteria;
  const awardCriterionCount = awardCriteria?.bestPriceQualityRatio?.criteria?.length ?? 0;
  const awardModelLabel = awardCriteria?.lowestPrice
    ? t("Lowest price")
    : awardCriteria?.bestPriceQualityRatio
      ? t("Best price-quality ratio")
      : null;
  const minimumRequirementCount = state.eventData.minimumRequirements?.length ?? 0;
  const [deadlineStatus, ...otherDeadlineStatuses] = orderedByUnmet([
    readinessFor(FieldKeys.BidSubmissionDeadlinePresent),
    readinessFor(FieldKeys.BidSubmissionDeadlineInFuture),
  ]);
  // The badge follows `status` alone, and both award-criteria keys report the generic REQUIRED code
  // that SummaryField leaves to the badge. Lead with the first unmet one so an incomplete model is
  // always the one flagged — otherwise satisfied weights would show a green check while missing
  // descriptions silently hold Publish back.
  const criteriaIncluded = includesAwardCriteria(state.eventData);
  const requirementsIncluded = includesMinimumRequirements(state.eventData);
  const [awardCriteriaStatus, ...otherAwardCriteriaStatuses] = orderedByUnmet([
    ...(criteriaIncluded ? [readinessFor(FieldKeys.AwardCriteriaPresentWhenIncluded)] : []),
    ...(awardCriteria
      ? [readinessFor(FieldKeys.AwardCriteriaWeightsValid), readinessFor(FieldKeys.AwardCriteriaDescriptionsPresent)]
      : []),
  ]);

  const publish = async () => {
    try {
      await publishPrivate({ eventId }).unwrap();
      toast({
        title: t("Event published!"),
        description: t("Your event is now live, and the invitations have been sent"),
        status: "success",
      });
      navigate(urls.v2.sourcingEvents.view.go(eventId));
    } catch (e) {
      trigger(e);
    }
  };

  const sendChange = async () => {
    if (!changeDraft) return;
    try {
      const published = await submitChange({ eventId, revisionId: changeDraft.id }).unwrap();
      const invitationsOnly =
        published.changedAreas?.length === 1 && published.changedAreas[0] === ChangedArea.Invitations;
      toast({
        title: t("The change has been published"),
        description: invitationsOnly
          ? t("The suppliers already invited were not notified.")
          : t("The invited suppliers have been told what changed."),
        status: "success",
      });
      navigate(urls.v2.sourcingEvents.view.go(eventId));
    } catch (e) {
      trigger(e);
    }
  };

  const discardChange = async () => {
    if (!changeDraft) return;
    try {
      await cancelChange({ eventId, revisionId: changeDraft.id }).unwrap();
      toast({ title: t("The change was discarded"), status: "success" });
      navigate(urls.v2.sourcingEvents.view.go(eventId));
    } catch (e) {
      trigger(e);
    }
  };

  const confirmDiscardChange = () => {
    confirmationModal.show({
      title: t("Discard the change?"),
      description: t("The competition goes back to what the suppliers were last told."),
      confirmButtonText: t("Discard the change") ?? "",
      onConfirmed: discardChange,
    });
  };

  const confirmSendChange = () => {
    confirmationModal.show({
      title: t("Send the change?"),
      description: t(
        "The suppliers already invited are told what changed so they can update their bid. Adding an invitation on its own does not notify them.",
      ),
      confirmButtonText: t("Send the change") ?? "",
      onConfirmed: sendChange,
    });
  };

  const confirmPublish = () => {
    confirmationModal.show({
      title: t("Publish the procurement?"),
      description: t("The invited organizations are notified immediately."),
      confirmButtonText: t("Publish") ?? "",
      onConfirmed: publish,
    });
  };

  return (
    <Box pb="8" pt="10">
      {confirmationModal.modal}
      {isChangeMode && (
        <Alert status="info" borderRadius="md" mb="6">
          <AlertIcon />
          {t(
            "You are changing a published competition. The suppliers already invited are told what changed, unless the only change is a new invitation.",
          )}
        </Alert>
      )}
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <Heading size="md" mb="4">
          {isChangeMode ? t("Review the change before sending it") : t("Review your event before publishing")}
        </Heading>

        <SummaryField
          label={t("Title")}
          status={readinessFor(FieldKeys.TitlePresent)}
          value={state.eventData.title || <EmptyValue text={t("Not provided")} />}
        />
        <SummaryField
          label={t("Description")}
          status={readinessFor(FieldKeys.DescriptionPresent)}
          value={
            state.eventData.description && !isTiptapContentEmpty(state.eventData.description) ? (
              <TipTap content={state.eventData.description} editable={false} hasBorder={false} padding="0" />
            ) : (
              <EmptyValue text={t("Not provided")} />
            )
          }
        />
        <SummaryField
          label={t("Deadline")}
          status={deadlineStatus}
          extraStatuses={otherDeadlineStatuses}
          value={
            state.eventData.bidSubmissionDeadline ? (
              displayDateWithTime(state.eventData.bidSubmissionDeadline, t("at"))
            ) : (
              <EmptyValue text={t("Not provided")} />
            )
          }
        />
        {hasProductAccess && (
          <SummaryField
            label={t("Products")}
            status={productsIncluded ? readinessFor(FieldKeys.ProductsPresentWhenIncluded) : undefined}
            value={
              productGroups.length > 0 ? (
                <Text>
                  {t("{{count}} product groups", { count: productGroups.length })}
                  {` · ${t("{{count}} products", { count: productCount })}`}
                </Text>
              ) : (
                <EmptyValue text={productsIncluded ? t("No products added yet") : t("Not included")} />
              )
            }
          />
        )}
        {/* Both sections are optional, and a section left out carries no readiness badge. One the
            buyer switched on is held to the publish gate: it has to hold something. */}
        <SummaryField
          label={terms.awardCriteria}
          status={awardCriteriaStatus}
          extraStatuses={otherAwardCriteriaStatuses}
          value={
            awardModelLabel ? (
              <Text>
                {awardModelLabel}
                {awardCriterionCount > 0
                  ? ` · ${terms.awardCriteriaCount(awardCriterionCount)}`
                  : ""}
              </Text>
            ) : (
              // "Not included" would contradict the switch the buyer just turned on — an included
              // section that is still empty is unfinished, not opted out.
              <EmptyValue text={criteriaIncluded ? terms.noAwardModelChosenYet : t("Not included")} />
            )
          }
        />
        <SummaryField
          label={t("Minimum requirements")}
          status={requirementsIncluded ? readinessFor(FieldKeys.MinimumRequirementsPresentWhenIncluded) : undefined}
          value={
            minimumRequirementCount > 0 ? (
              t("{{count}} minimum requirements", { count: minimumRequirementCount })
            ) : (
              <EmptyValue text={requirementsIncluded ? t("None added yet") : t("Not included")} />
            )
          }
        />
        <SummaryField
          label={t("Invitations")}
          status={readinessFor(FieldKeys.AtLeastOneInvitation)}
          value={
            invitedCount > 0 ? (
              <Text>
                {t("{{count}} organizations invited", { count: invitedCount })}
                {emailInviteCount > 0 ? ` · ${t("{{count}} invited by email", { count: emailInviteCount })}` : ""}
              </Text>
            ) : (
              <EmptyValue text={t("No organizations have been invited yet.")} />
            )
          }
        />
        <SummaryField
          label={t("Documents")}
          value={
            documentCount > 0 ? (
              t("{{count}} documents attached", { count: documentCount })
            ) : (
              <EmptyValue text={t("No documents attached")} />
            )
          }
        />
      </Box>

      {isChangeMode && (
        <Flex justify="flex-start" mb="6">
          <Button variant="outline" colorScheme="red" onClick={confirmDiscardChange} isLoading={isDiscarding}>
            {t("Discard the change")}
          </Button>
        </Flex>
      )}

      <StepNavigation
        onPrevious={previousStep}
        onNext={isChangeMode ? confirmSendChange : confirmPublish}
        nextLabel={isChangeMode ? t("Send the change") : t("Publish")}
        nextIsAction
        isNextDisabled={
          readinessLoading ||
          readinessError ||
          !isPublishReady ||
          isPublishing ||
          isSendingChange
        }
        isNextLoading={isPublishing || isSendingChange}
        nextTooltip={
          !isPublishReady && !readinessLoading
            ? t("Fill in all required fields above to enable publishing")
            : undefined
        }
      />
    </Box>
  );
};
